import { getErrorMessage } from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BackButton,
    BackButtonIconType,
    BodyLargeEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    ControlledPicker,
    Form,
    isWeb,
    SafeAreaView,
    SmoothieScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { captureException, captureMessage } from '../../../logger/Logger';
import useSimulateAcat, {
    AcatTestScenario,
    type SimulateAcatRequest,
} from '../../hooks/useSimulateAcat';

const scenarioOptions = [
    {
        label: 'Incoming Full Transfer',
        value: AcatTestScenario.INCOMING_FULL_TRANSFER,
    },
    {
        label: 'Incoming Partial Transfer',
        value: AcatTestScenario.INCOMING_PARTIAL_TRANSFER,
    },
    {
        label: 'Incoming Full (Equity + Cash + MF)',
        value: AcatTestScenario.INCOMING_FULL_EQTY_CASH_MF,
    },
    {
        label: 'Incoming Full (Equity + Cash + MF + FI)',
        value: AcatTestScenario.INCOMING_FULL_EQTY_CASH_MF_FI,
    },
    {
        label: 'Incoming PTR (Equity + Cash + MF)',
        value: AcatTestScenario.INCOMING_PTR_EQTY_CASH_MF,
    },
    {
        label: 'Incoming PTR (Equity + Cash + MF + FI)',
        value: AcatTestScenario.INCOMING_PTR_EQTY_CASH_MF_FI,
    },
    {
        label: 'Incoming Full Reject (Receiver)',
        value: AcatTestScenario.RECEIVER_IN_FULL_REJECT,
    },
    {
        label: 'Incoming Full Reject (Deliverer)',
        value: AcatTestScenario.DELIVER_INC_FULL_REJECT,
    },
    {
        label: 'Incoming Full (Cash Only)',
        value: AcatTestScenario.INCOMING_FULL_CASH_ONLY,
    },
    {
        label: 'Incoming Full (Equity Only)',
        value: AcatTestScenario.INCOMING_FULL_EQTY_ONLY,
    },
];

const acatSimulationSchema = z.object({
    apexAccountId: z.string().min(1, 'Apex Account ID is required'),
    scenario: z.nativeEnum(AcatTestScenario),
    comment: z.string().optional(),
});

type AcatSimulationSchema = z.infer<typeof acatSimulationSchema>;

const SimulateAcatTransfer = () => {
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const toast = useToastController();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastResponse, setLastResponse] = useState<string | null>(null);

    const { mutateAsync: simulateAcat } = useSimulateAcat();

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<AcatSimulationSchema>({
        resolver: zodResolver(acatSimulationSchema),
        defaultValues: {
            apexAccountId: apexAccountId ?? '',
            scenario: AcatTestScenario.INCOMING_FULL_TRANSFER,
            comment: '',
        },
    });

    const onSubmit: SubmitHandler<AcatSimulationSchema> = async ({
        apexAccountId: accountId,
        scenario,
        comment,
    }) => {
        setIsSubmitting(true);

        toast.show('Submitting ACAT simulation...', {
            duration: 3000,
        });

        try {
            const request: SimulateAcatRequest = {
                apexAccountId: accountId,
                scenario,
                ...(comment != null && comment !== '' && { comment }),
            };

            const response = await simulateAcat({ body: request });

            setLastResponse(JSON.stringify(response, null, 2));

            const successMessage =
                response.message !== ''
                    ? `ACAT simulated: ${response.message}`
                    : 'ACAT simulation submitted successfully';
            toast.show(successMessage, {
                duration: 10000,
            });
            captureMessage(`ACAT simulated: ${JSON.stringify(response)}`);
        } catch (e) {
            captureException(e);
            setLastResponse(
                JSON.stringify({ error: getErrorMessage(e) }, null, 2),
            );
            toast.show(`Error! ${getErrorMessage(e)}`, {
                duration: 10000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (apexAccountId == null) {
        captureException(
            new Error('Missing apexAccountId query param for ACAT simulation'),
        );
        router.back();
        return null;
    }

    return (
        <SafeAreaView>
            <SmoothieScrollView>
                <View>
                    <BodyLargeEmphasized alignSelf="center">
                        Simulate ACAT Transfer
                    </BodyLargeEmphasized>
                    <View marginTop={-26} marginBottom={24}>
                        <BackButton
                            fallbackRoute="/home/investment"
                            iconType={BackButtonIconType.ARROW_LEFT}
                        />
                    </View>
                </View>
                <YStack
                    maxWidth={700}
                    alignSelf="center"
                    marginTop={isWeb ? 24 : 0}
                    marginHorizontal={16}
                >
                    <View marginBottom={16}>
                        <Form onSubmit={handleSubmit(onSubmit)} flex={1}>
                            <YStack gap={12} flex={1} minWidth={500}>
                                <ControlledInput
                                    name="apexAccountId"
                                    label="Apex Account ID"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    editable={false}
                                    disabled
                                />
                                <ControlledPicker
                                    name="scenario"
                                    label="Scenario"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    options={scenarioOptions}
                                />
                                <ControlledInput
                                    name="comment"
                                    label="Comment (optional)"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                />
                                <Button
                                    tone="positive"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    text="Simulate ACAT Transfer"
                                    onPress={handleSubmit(onSubmit)}
                                    disabled={!isValid || isSubmitting}
                                />
                                {lastResponse != null && (
                                    <View
                                        backgroundColor="$gray100"
                                        padding={12}
                                        borderRadius={8}
                                        marginTop={16}
                                    >
                                        <BodyLargeEmphasized marginBottom={8}>
                                            Response:
                                        </BodyLargeEmphasized>
                                        <BodySmallRegular
                                            fontFamily="$monoBody"
                                            whiteSpace="pre-wrap"
                                        >
                                            {lastResponse}
                                        </BodySmallRegular>
                                    </View>
                                )}
                            </YStack>
                        </Form>
                    </View>
                </YStack>
            </SmoothieScrollView>
        </SafeAreaView>
    );
};

export default SimulateAcatTransfer;
